import { AbortAPIError } from '../../utils/newFetch/HTTPClient';
import ServiceMapAPI from '../../utils/newFetch/ServiceMapAPI';
import { resolveParamsForParkingFetch } from '../../utils/parking';
import {
  dataStructure,
  geographicalDistricts,
  groupDistrictData,
  parkingUnitCategoryIds,
  parseDistrictGeometry,
} from '../../views/AreaView/utils/districtDataHelper';

export const setHighlightedDistrict = (district) => ({
  type: 'SET_DISTRICT_HIGHLIGHT',
  district,
});

export const setSelectedDistrictType = (district) => ({
  type: 'SET_SELECTED_DISTRICT_TYPE',
  district,
});

const setDistrictData = (data) => ({
  type: 'SET_DISTRICT_DATA',
  data,
});

const updateDistrictData = (type, data, period) => ({
  type: 'UPDATE_DISTRICT_DATA',
  districtType: type,
  period,
  data,
});

export const setDistrictAddressData = (data) => ({
  type: 'SET_DISTRICT_ADDRESS_DATA',
  data,
});

export const setSelectedSubdistricts = (districts) => ({
  type: 'SET_SELECTED_SUBDISTRICTS',
  districts,
});

export const addSelectedDistrictService = (district) => ({
  type: 'ADD_SELECTED_DISTRICT_SERVICE',
  district,
});

export const removeSelectedDistrictService = (data) => ({
  type: 'REMOVE_SELECTED_DISTRICT_SERVICE',
  data,
});

export const setSelectedDistrictServices = (services) => ({
  type: 'SET_SELECTED_DISTRICT_SERVICES',
  services,
});

export const setSelectedParkingAreas = (areas) => ({
  type: 'SET_SELECTED_PARKING_AREAS',
  areas,
});

export const addSelectedParkingArea = (areaID) => ({
  type: 'ADD_SELECTED_PARKING_AREA',
  areaID,
});

export const removeSelectedParkingArea = (areaID) => ({
  type: 'REMOVE_SELECTED_PARKING_AREA',
  areaID,
});

const addOpenItem = (item) => ({
  type: 'ADD_OPEN_ITEM',
  item,
});

const removeOpenItem = (item) => ({
  type: 'REMOVE_OPEN_ITEM',
  item,
});

export const setMapState = (object) => ({
  type: 'SET_MAP_STATE',
  object,
});

const updateParkingAreas = (areas) => ({
  type: 'UPDATE_PARKING_AREAS',
  areas,
});

export const setParkingUnits = (parkingUnitCategoryId, units) => ({
  type: 'SET_PARKING_UNITS',
  units,
  parkingUnitCategoryId,
});

const startUnitFetch = (node) => ({
  type: 'START_UNIT_FETCH',
  node,
});

const updateFetchProggress = (data) => ({
  type: 'UPDATE_FETCH_PROGRESS',
  count: data.count,
  max: data.max,
});

const endUnitFetch = (data) => ({
  type: 'END_UNIT_FETCH',
  node: data.nodeID,
  units: data.units,
  isLastFetch: data.isLastFetch,
});

const startDistrictFetch = (districtType) => ({
  type: 'START_DISTRICT_FETCH',
  districtType,
});

const endDistrictFetch = (districtType) => ({
  type: 'END_DISTRICT_FETCH',
  districtType,
});

export const fetchDistrictGeometry = (type, period) => async (dispatch) => {
  dispatch(startDistrictFetch(type));
  const smAPI = new ServiceMapAPI();
  try {
    const boundaries = await smAPI.areaGeometry(type);
    let filteredData = parseDistrictGeometry(boundaries);
    if (period) {
      // Filter with start and end year
      const start = period.slice(0, 4);
      const end = period.slice(-4);
      const yearFilteredData = filteredData.filter((item) => {
        if (item.extra?.schoolyear) {
          if (item.extra?.schoolyear === period) {
            return true;
          }
          return false;
        }
        if (item.start.slice(0, 4) === start && item.end.slice(0, 4) === end) {
          return true;
        }
        return false;
      });
      filteredData = yearFilteredData;
    }
    dispatch(updateDistrictData(type, filteredData, period));
  } catch (e) {
    if (!(e instanceof AbortAPIError))
      console.warn('fetchDistrictGeometry error:', e);
  } finally {
    dispatch(endDistrictFetch(type));
  }
};

export const fetchDistricts =
  (selected, single, period) => async (dispatch) => {
    const categories = single
      ? selected
      : dataStructure
          .map((obj) => obj.districts.map((item) => item.id))
          .flat()
          .join(',');

    const fetchKey = single ? selected : 'all';
    dispatch(startDistrictFetch(fetchKey));
    const smAPI = new ServiceMapAPI();
    try {
      const areas = await smAPI.areas(categories);
      const groupedData = groupDistrictData(areas);
      dispatch(setDistrictData(groupedData));
      if (selected) dispatch(fetchDistrictGeometry(selected, period));
    } catch (e) {
      if (!(e instanceof AbortAPIError))
        console.warn('fetchDistricts error:', e);
    } finally {
      dispatch(endDistrictFetch(fetchKey));
    }
  };

export const fetchDistrictUnitList = (nodeID) => async (dispatch, getState) => {
  try {
    let lastTotal = 0;
    let maxInitialized = false;
    const progressUpdate = (count, max) => {
      if (!maxInitialized) {
        // Initialize max once; getConcurrent reports cumulative counts.
        dispatch(updateFetchProggress({ max }));
        maxInitialized = true;
      }

      if (typeof count === 'number') {
        // getConcurrent reports cumulative totals; reducer expects deltas.
        const increment = count - lastTotal;
        if (increment > 0) {
          lastTotal = count;
          dispatch(updateFetchProggress({ count: increment }));
        } else {
          // Ignore stale/duplicate updates.
          lastTotal = Math.max(lastTotal, count);
        }
      }
    };
    const smAPI = new ServiceMapAPI();
    smAPI.setOnProgressUpdate(progressUpdate);
    dispatch(startUnitFetch(nodeID));
    const units = await smAPI.areaUnits(nodeID);
    units.forEach((unit) => {
      unit.object_type = 'unit';
      unit.division_id = nodeID;
    });

    // If multiple districts fetching at the same time, last result needs to end search
    const { nodesFetching } = getState().districts.unitFetch;
    let isLastFetch;
    if (nodesFetching.length === 1) isLastFetch = true;

    dispatch(endUnitFetch({ nodeID, units, isLastFetch }));
  } catch (e) {
    console.warn(e);
    dispatch(endUnitFetch({ nodeID, units: [], isLastFetch: true }));
  }
};

export const fetchParkingAreaGeometry = (areaId) => async (dispatch) => {
  const type = 'parking_area';
  const options = resolveParamsForParkingFetch(areaId);

  dispatch(startDistrictFetch(areaId));
  const smAPI = new ServiceMapAPI();
  try {
    const areas = await smAPI.areaGeometry(type, options);
    dispatch(updateParkingAreas(areas));
  } catch (e) {
    if (!(e instanceof AbortAPIError))
      console.warn('fetchParkingAreaGeometry error:', e);
  } finally {
    dispatch(endDistrictFetch(areaId));
  }
};

export const fetchParkingUnits = (parkingCategoryId) => async (dispatch) => {
  if (!parkingUnitCategoryIds.find((id) => id === parkingCategoryId)) {
    throw new Error(`Parking category ${parkingCategoryId} is not supported.`);
  }
  const municipality = parkingCategoryId.split('-')[0];
  const id = parkingCategoryId.split('-')[1];
  const districtType = `parkingUnits-${parkingCategoryId}`;
  dispatch(startDistrictFetch(districtType));
  const smAPI = new ServiceMapAPI();
  try {
    const units = await smAPI.serviceNodeSearch('ServiceTree', id, {
      language: 'fi',
      municipality,
    });
    units.forEach((item) => {
      item.object_type = 'unit';
    });
    dispatch(setParkingUnits(parkingCategoryId, units));
  } catch (e) {
    if (!(e instanceof AbortAPIError))
      console.warn('fetchParkingUnits error:', e);
  } finally {
    dispatch(endDistrictFetch(districtType));
  }
};

export const fetchParkingGarages = () => fetchParkingUnits('helsinki-531');
export const fetchSharedCarParking = () => fetchParkingUnits('vantaa-2204');
export const fetchAccessibleStreetParking = () =>
  fetchParkingUnits('vantaa-2207');

export const handleOpenItems = (id) => (dispatch, getState) => {
  const { openItems } = getState().districts;
  if (openItems.includes(id)) {
    dispatch(removeOpenItem(id));
  } else {
    dispatch(addOpenItem(id));
  }
};

export const handleOpenGeographicalCategory = (id) => (dispatch, getState) => {
  const { openItems } = getState().districts;
  const stateCategory = openItems.find((item) =>
    geographicalDistricts.includes(item)
  );
  if (stateCategory) {
    dispatch(removeOpenItem(stateCategory));
  }
  if (id) {
    dispatch(addOpenItem(id));
  }
};
